import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Chip } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type { AttributeType } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AttributeValueFormModal } from '../attributeValueFormModal';
import { VALUE_STATUS_CHIP } from '../attributesMeta';
import type { useAttributeValuesSheet } from './useAttributeValuesSheet';

type AttributeValuesSheetViewProps = {
    isOpen: boolean;
    onClose: () => void;
    sheet: ReturnType<typeof useAttributeValuesSheet>;
    type: AttributeType | null;
};

export const AttributeValuesSheetView: React.FC<
    AttributeValuesSheetViewProps
> = ({ isOpen, onClose, sheet, type }) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            subtitle: t(
                'attributes.components.dictionary.valuesSheet.subtitle',
            ),
            addValue: t(
                'attributes.components.dictionary.valuesSheet.addValue',
            ),
            empty: t('attributes.components.dictionary.empty.values'),
            aliasPlaceholder: t(
                'attributes.components.dictionary.valuesSheet.aliasPlaceholder',
            ),
            addAlias: t(
                'attributes.components.dictionary.valuesSheet.addAlias',
            ),
            removeAlias: t(
                'attributes.components.dictionary.valuesSheet.removeAlias',
            ),
            moveUp: t('attributes.components.dictionary.valuesSheet.moveUp'),
            moveDown: t(
                'attributes.components.dictionary.valuesSheet.moveDown',
            ),
            edit: t('attributes.components.dictionary.actions.edit'),
            delete: t('attributes.components.dictionary.actions.delete'),
            status: {
                Active: t('attributes.components.dictionary.status.active'),
                Pending: t('attributes.components.dictionary.status.pending'),
            },
        }),
        [t],
    );

    return (
        <>
            <Sheet
                isOpen={isOpen}
                onOpenChange={(open) => !open && onClose()}
                placement="right"
            >
                <Sheet.Backdrop>
                    <Sheet.Content className="w-full sm:max-w-[480px]">
                        <Sheet.Dialog
                            {...getNodeTestIdProp('attribute-values-sheet')}
                        >
                            <Sheet.CloseTrigger />
                            <Sheet.Header
                                {...getNodeTestIdProp(
                                    'attribute-values-sheet-header',
                                )}
                            >
                                <Sheet.Heading
                                    {...getNodeTestIdProp(
                                        'attribute-values-sheet-title',
                                    )}
                                >
                                    {type?.name ?? ''}
                                </Sheet.Heading>
                                <p className="font-mono text-xs text-muted">
                                    {type?.slug ?? ''} · {copy.subtitle}
                                </p>
                            </Sheet.Header>
                            <Sheet.Body
                                className="flex flex-col gap-4"
                                {...getNodeTestIdProp(
                                    'attribute-values-sheet-body',
                                )}
                            >
                                <Button
                                    fullWidth
                                    onPress={sheet.onAddValue}
                                    variant="secondary"
                                    {...getNodeTestIdProp(
                                        'attribute-values-add',
                                    )}
                                >
                                    <Icon icon="gravity-ui:plus" />
                                    {copy.addValue}
                                </Button>
                                {sheet.values.length === 0 ? (
                                    <div
                                        className="flex flex-col items-center gap-2 py-10 text-muted"
                                        {...getNodeTestIdProp(
                                            'attribute-values-empty',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            className="size-7 text-default-500"
                                            icon="gravity-ui:list-ul"
                                        />
                                        <span className="text-sm">
                                            {copy.empty}
                                        </span>
                                    </div>
                                ) : (
                                    <ul
                                        className="flex flex-col gap-3"
                                        {...getNodeTestIdProp(
                                            'attribute-values-list',
                                        )}
                                    >
                                        {sheet.values.map((value, index) => (
                                            <li
                                                className="flex flex-col gap-2 rounded-xl border border-border bg-surface-secondary p-3"
                                                key={value.id}
                                                {...getNodeTestIdProp(
                                                    `attribute-value-item-${value.id}`,
                                                )}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="flex flex-col">
                                                        <Button
                                                            aria-label={
                                                                copy.moveUp
                                                            }
                                                            isDisabled={
                                                                index === 0 ||
                                                                sheet.isReordering
                                                            }
                                                            isIconOnly
                                                            onPress={() =>
                                                                sheet.onMoveValue(
                                                                    value.id,
                                                                    'up',
                                                                )
                                                            }
                                                            size="sm"
                                                            variant="tertiary"
                                                            {...getNodeTestIdProp(
                                                                `attribute-value-move-up-${value.id}`,
                                                            )}
                                                        >
                                                            <Icon icon="gravity-ui:chevron-up" />
                                                        </Button>
                                                        <Button
                                                            aria-label={
                                                                copy.moveDown
                                                            }
                                                            isDisabled={
                                                                index ===
                                                                    sheet.values
                                                                        .length -
                                                                        1 ||
                                                                sheet.isReordering
                                                            }
                                                            isIconOnly
                                                            onPress={() =>
                                                                sheet.onMoveValue(
                                                                    value.id,
                                                                    'down',
                                                                )
                                                            }
                                                            size="sm"
                                                            variant="tertiary"
                                                            {...getNodeTestIdProp(
                                                                `attribute-value-move-down-${value.id}`,
                                                            )}
                                                        >
                                                            <Icon icon="gravity-ui:chevron-down" />
                                                        </Button>
                                                    </div>
                                                    <div className="flex min-w-0 flex-1 flex-col">
                                                        <span className="text-sm font-medium text-foreground">
                                                            {value.name}
                                                        </span>
                                                        <span className="font-mono text-xs text-muted">
                                                            {value.slug}
                                                        </span>
                                                    </div>
                                                    <Chip
                                                        color={
                                                            VALUE_STATUS_CHIP[
                                                                value.status
                                                            ].color
                                                        }
                                                        size="sm"
                                                        variant="soft"
                                                    >
                                                        <Chip.Label>
                                                            {
                                                                copy.status[
                                                                    value.status
                                                                ]
                                                            }
                                                        </Chip.Label>
                                                    </Chip>
                                                    <Button
                                                        aria-label={copy.edit}
                                                        isIconOnly
                                                        onPress={() =>
                                                            sheet.onEditValue(
                                                                value.id,
                                                            )
                                                        }
                                                        size="sm"
                                                        variant="tertiary"
                                                        {...getNodeTestIdProp(
                                                            `attribute-value-edit-${value.id}`,
                                                        )}
                                                    >
                                                        <Icon icon="gravity-ui:pencil" />
                                                    </Button>
                                                    <Button
                                                        aria-label={copy.delete}
                                                        isIconOnly
                                                        onPress={() =>
                                                            sheet.onDeleteValue(
                                                                value.id,
                                                            )
                                                        }
                                                        size="sm"
                                                        variant="danger-soft"
                                                        {...getNodeTestIdProp(
                                                            `attribute-value-delete-${value.id}`,
                                                        )}
                                                    >
                                                        <Icon icon="gravity-ui:trash-bin" />
                                                    </Button>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {value.aliases.map(
                                                        (alias) => (
                                                            <span
                                                                className="inline-flex items-center gap-1 rounded-md bg-surface px-2 py-0.5 text-xs text-muted"
                                                                key={alias.id}
                                                                {...getNodeTestIdProp(
                                                                    `attribute-value-alias-${alias.id}`,
                                                                )}
                                                            >
                                                                <span className="font-mono">
                                                                    {
                                                                        alias.alias
                                                                    }
                                                                </span>
                                                                <button
                                                                    aria-label={
                                                                        copy.removeAlias
                                                                    }
                                                                    className="text-muted hover:text-danger"
                                                                    onClick={() =>
                                                                        sheet.onDeleteAlias(
                                                                            value.id,
                                                                            alias.id,
                                                                        )
                                                                    }
                                                                    type="button"
                                                                    {...getNodeTestIdProp(
                                                                        `attribute-value-alias-remove-${alias.id}`,
                                                                    )}
                                                                >
                                                                    <Icon
                                                                        className="size-3"
                                                                        icon="gravity-ui:xmark"
                                                                    />
                                                                </button>
                                                            </span>
                                                        ),
                                                    )}
                                                    <input
                                                        aria-label={
                                                            copy.aliasPlaceholder
                                                        }
                                                        className="min-w-24 flex-1 rounded-md border border-border bg-surface px-2 py-1 text-xs text-foreground placeholder:text-muted focus:outline-none"
                                                        onChange={(event) =>
                                                            sheet.onAliasDraftChange(
                                                                value.id,
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                        onKeyDown={(event) => {
                                                            if (
                                                                event.key ===
                                                                'Enter'
                                                            ) {
                                                                event.preventDefault();
                                                                sheet.onAddAlias(
                                                                    value.id,
                                                                );
                                                            }
                                                        }}
                                                        placeholder={
                                                            copy.aliasPlaceholder
                                                        }
                                                        type="text"
                                                        value={
                                                            sheet.aliasDrafts[
                                                                value.id
                                                            ] ?? ''
                                                        }
                                                        {...getNodeTestIdProp(
                                                            `attribute-value-alias-input-${value.id}`,
                                                        )}
                                                    />
                                                    <Button
                                                        aria-label={
                                                            copy.addAlias
                                                        }
                                                        isIconOnly
                                                        onPress={() =>
                                                            sheet.onAddAlias(
                                                                value.id,
                                                            )
                                                        }
                                                        size="sm"
                                                        variant="tertiary"
                                                        {...getNodeTestIdProp(
                                                            `attribute-value-alias-add-${value.id}`,
                                                        )}
                                                    >
                                                        <Icon icon="gravity-ui:plus" />
                                                    </Button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Sheet.Body>
                        </Sheet.Dialog>
                    </Sheet.Content>
                </Sheet.Backdrop>
            </Sheet>
            <AttributeValueFormModal
                error={sheet.valueFormError}
                initialValues={sheet.valueFormInitialValues}
                isOpen={sheet.isValueFormOpen}
                isSubmitting={sheet.isValueSubmitting}
                mode={sheet.valueFormMode}
                onClose={sheet.onCloseValueForm}
                onSubmit={sheet.onSubmitValue}
            />
        </>
    );
};
